// src/pages/Consent.tsx
import React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { POLICY_VERSION, upsertConsentToDb, setLocalConsent } from '../lib/policy';

export default function Consent() {
  const [checked, setChecked] = React.useState(false);
  const [scrolledEnough, setScrolledEnough] = React.useState(false);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const next = sp.get('next') || '/dashboard';
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  const onApprove = async () => {
    if (!checked || !scrolledEnough) return;
    setLoading(true);
    try {
      await upsertConsentToDb(supabase);
      setLocalConsent();
      navigate(next, { replace: true });
    } catch (e) {

      setLoading(false);
    }
  };

  // גלילה: עדכון התקדמות + “קראת מספיק”
  const handleScroll = React.useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const y = el.scrollTop;
    const p = Math.min(1, Math.max(0, max > 0 ? y / max : 1));
    setScrollProgress(p);
    setScrolledEnough(p >= 0.9);
  }, []);

  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    handleScroll();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const canApprove = checked && scrolledEnough && !loading;

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-3xl"
        dir="rtl"
      >
        {/* כותרת עליונה */}
        <div className="mb-4 sm:mb-6 flex items-center justify-between">
          <button
            onClick={() => (window.history.state && (window.history.state as any).idx > 0 ? navigate(-1) : navigate('/login'))}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 active:scale-[0.99] transition-all text-sm"
          >
            <ArrowRight className="h-5 w-5" />
            חזרה
          </button>
          <span className="text-[11px] text-gray-500">גרסה: {POLICY_VERSION}</span>
        </div>

        {/* כרטיס המדיניות */}
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-gray-100">
          <div className="p-5 sm:p-6 border-b">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">אישור מדיניות פרטיות</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  אנא קרא/י את המדיניות עד הסוף ואשר/י כדי להמשיך.
                </p>
              </div>
            </div>

            {/* פס התקדמות גלילה */}
            <div className="mt-4">
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-indigo-600 transition-[width] duration-150"
                  style={{ width: `${Math.round(scrollProgress * 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                <span>התקדמות קריאה</span>
                <span>{Math.round(scrollProgress * 100)}%</span>
              </div>
            </div>
          </div>

          {/* תוכן המדיניות (גליל) */}
          <div className="p-5 sm:p-6">
            <div
              ref={contentRef}
              className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto pr-3 sm:pr-4"
            >
              <article className="prose prose-sm sm:prose-base max-w-none prose-headings:font-semibold">
                <h2>מדיניות פרטיות – LoyalFlow</h2>
                <p className="text-sm text-gray-500">עודכן: 12.10.2025 | גרסה: {POLICY_VERSION}</p>

                <p>
                  ברוך/ה הבא/ה ל-LoyalFlow. המדיניות שלהלן מסבירה כיצד אנו אוספים, משתמשים, שומרים ומשתפים מידע שמתקבל בעת שימושך בשירות.
                </p>

                <h3>איזה מידע אנו אוספים</h3>
                <ul>
                  <li>פרטי חשבון (אימייל, שם, מזהה משתמש)</li>
                  <li>נתוני שימוש במערכת (לוגים, פעולות, ועוד)</li>
                  <li>מידע הנדרש לצורכי סליקה/חשבוניות (בהתאם לחוק)</li>
                </ul>

                <h3>למה אנו משתמשים במידע</h3>
                <p>מתן השירות, אבטחה, שיפור חוויית משתמש, עמידה בדרישות חוק וכו׳.</p>

                <h3>שיתוף מידע</h3>
                <p>ספקי משנה (למשל תשלומים), בהתאם להסכמים ולחוק. לא נמכור נתונים לצד ג׳.</p>

                <h3>אבטחת מידע</h3>
                <p>נוקטים באמצעים סבירים (טכנולוגיים וארגוניים) לשמירת המידע.</p>

                <h3>זכויות משתמש</h3>
                <p>זכות לעיון/מחיקה/תיקון בהתאם לחוק. לפניות: support@loyalflow.co.il</p>

                <h3>שינויים במדיניות</h3>
                <p>נעדכן גרסה ונציג הודעה. המשך שימוש משמעו הסכמה לגרסה המעודכנת.</p>

                <p className="text-xs text-gray-400 mt-6">
                  גרסה זו מוצגת לצרכי הסכמה בלבד. לצפייה בנוסח המלא בדף ייעודי:
                  {' '}
                  <Link to="/privacy" target="_blank" className="text-indigo-600 underline underline-offset-2">
                    מדיניות הפרטיות המלאה
                  </Link>.
                </p>
              </article>
            </div>

            {/* אינדיקציה שקראת עד הסוף */}
            <div className="mt-4 flex items-center gap-2 text-sm">
              {scrolledEnough ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-gray-300" />
              )}
              <span className={scrolledEnough ? 'text-green-700' : 'text-gray-500'}>
                {scrolledEnough ? 'קראת את המסמך כמעט עד הסוף' : 'גלול/י עד לסוף המסמך כדי להמשיך'}
              </span>
            </div>

            {/* צ׳קבוקס + כפתורים */}
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <label className="flex items-start gap-2 text-sm select-none">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <span>קראתי ואני מאשר/ת (גרסה {POLICY_VERSION})</span>
              </label>

              <div className="flex items-center gap-2">
                <Link
                  to="/privacy"
                  target="_blank"
                  className="px-3 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
                >
                  פתיחת מדיניות מלאה
                </Link>
                <motion.button
                  whileHover={canApprove ? { scale: 1.01 } : undefined}
                  whileTap={canApprove ? { scale: 0.99 } : undefined}
                  onClick={onApprove}
                  disabled={!canApprove}
                  className={`px-4 py-2 rounded-xl text-sm text-white transition-all ${
                    canApprove
                      ? 'bg-indigo-600 hover:bg-indigo-700 shadow-sm'
                      : 'bg-indigo-300 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'שומר...' : 'מאשר/ת וממשיך/ה'}
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
